import PropTypes from 'prop-types';
import React,  { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import FormHelperText from '@mui/material/FormHelperText';
import CustomInput from 'components/CustomInput/CustomInput.js';
/*
    Example:
    <SecretInput
        inputId='inputId'
        labelText='InputLabel'
        inputValue={tenant.secret}
        onValidSecret={(value) => {
            setSecret(value);
            setSecretState('success');
            setViewIsDirty(true)
        }}
        onInvalidSecret={() => {
            setSecretState('error');
            setViewIsDirty(true)
        }}
        validationRules={{
            minLength: 12,
            maxLength: 12,
            special: true,
            required: true,
            minUppercase: 1,
            alphanumeric: true,
            pattern: ''
        }}
        resetInput={resetInputs}
        validate={validate}
    ></SecretInput>
*/
function SecretInput({ labelText, inputId, inputValue, onValidSecret = () => {}, onInvalidSecret = () => {}, validationRules = {}, resetInput, validate}) {
  const inputRef = useRef(null);
  const [disableSecret, setDisableSecret] = useState(true);
  const [secretValue, setSecretValue] = useState((inputValue)? '*'.repeat(23): '');
  const [validationError, setvalidationError] = useState('');

  const handleInputChange = (event) => {
    const secret = event.target.value
    setSecretValue(secret);
    validateSecret(secret);
  };

  const validateSecret = (secret) => {
    let errMsg = '';
    if (validationRules.required !== undefined && validationRules.required && secret === '') {
      errMsg = `Input cannot be empty.`;
    } 
    else if (secret !== '') {
      if (validationRules.minLength !== undefined && validationRules.minLength > secret.length) {
        errMsg = `Input must be at least ${validationRules.minLength} characters long.`;
      }
      else if (validationRules.maxLength !== undefined && validationRules.maxLength < secret.length) {
        errMsg = `Input cannot exceed ${validationRules.maxLength} characters.`;
      } else if(validationRules.alphanumeric !== undefined && !/^(?=.*[a-zA-Z])(?=.*\d)/.test(secret)) {
        errMsg = `Input must contain at least one letter and number.`;
      }
      else if (validationRules.special !== undefined && !/(?=.*[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])/.test(secret)) {
        errMsg = `Input must contain at least one special characters.`;
      }
      else if (validationRules.pattern !== undefined && !validationRules.pattern.test(secret)) {
        errMsg = `Invalid format.`;
      }
      else if (validationRules.minUppercase !== undefined && validationRules.minUppercase > (secret.match(/[A-Z]/g)|| []).length) {
        errMsg = `Input must contain at least ${validationRules.minUppercase} uppercase letter.`;
      }
    } 
    errMsg == '' ? onValidSecret(secret) : onInvalidSecret();
    setvalidationError(errMsg)
  };

  const handleEditClick = () => {
    setSecretValue('');
    if (validationRules.required !== undefined && validationRules.required) {
      setvalidationError(`Input cannot be empty.`)
      onInvalidSecret();
    }
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.firstChild.focus();
      }
    }, 0);
    setDisableSecret(!disableSecret)
  };

  useEffect(() => {
    (inputValue) ? setSecretValue('*'.repeat(23)) : setSecretValue('');
    setvalidationError('')
    setDisableSecret(true);
  }, [resetInput])

  useEffect(() => {
    if(validate) {
      setDisableSecret(false);
      validateSecret(secretValue)
    }
  }, [validate])

  const defaultProps = {
    type: 'password',
    autoComplete: 'off',
    ref: inputRef,
    value: secretValue,
    disabled: disableSecret,
    onChange: handleInputChange,
    endAdornment: (
      <InputAdornment position='end'>
        <IconButton
          disableRipple
          size='small'
          onClick={() => handleEditClick()}
        >
          {disableSecret ? <EditIcon /> : <EditOffIcon />}
        </IconButton>
      </InputAdornment>
    ),
  }

  return (
    <>
      <CustomInput
        id={inputId}
        labelText={labelText}
        inputProps={defaultProps}
        error={validationError !== ''}
        formControlProps={{ 
          fullWidth: true,
        }}
      />
      {validationError && (
        <FormHelperText error>
          {validationError}
        </FormHelperText>
      )}
    </>
  );
};

SecretInput.propTypes = {
  inputId: PropTypes.string,
  labelText: PropTypes.string,
  inputValue: PropTypes.bool,
  onValidSecret: PropTypes.func,
  onInvalidSecret: PropTypes.func,
  validationRules: PropTypes.object,
  resetInput: PropTypes.number,
  validate: PropTypes.number
}

export default SecretInput